import { useCallback, useEffect, useRef, useState } from "react";

// While the bank is still confirming, check again on our own so the customer
// only has to wait, not refresh.
const RECHECK_INTERVAL_MS = 10000;
const MAX_AUTO_RECHECKS = 6;

// 200 = paid and booking confirmed.
// 202 = not confirmed; payment_status says "failed" only when the gateway has
// definitely declined it. Anything else is treated as still processing, so
// the customer is never told a charged payment failed.
const classify = (response) => {
  if (response?.status === 200) return "success";
  if (response?.data?.payment_status === "failed") return "failed";
  return "processing";
};

// status: "verifying" | "success" | "processing" | "failed"
const usePaymentVerification = (verifyFn, cartId, enabled = true) => {
  const [status, setStatus] = useState("verifying");
  const autoRechecks = useRef(0);
  const timer = useRef(null);

  const check = useCallback(() => {
    if (!cartId) return;
    clearTimeout(timer.current);
    verifyFn(cartId)
      .then((response) => {
        const result = classify(response);
        setStatus(result);
        if (result === "processing" && autoRechecks.current < MAX_AUTO_RECHECKS) {
          autoRechecks.current += 1;
          timer.current = setTimeout(check, RECHECK_INTERVAL_MS);
        }
      })
      .catch((error) => {
        console.error("Error verifying payment:", error);
        setStatus("processing");
        if (autoRechecks.current < MAX_AUTO_RECHECKS) {
          autoRechecks.current += 1;
          timer.current = setTimeout(check, RECHECK_INTERVAL_MS);
        }
      });
  }, [verifyFn, cartId]);

  useEffect(() => {
    if (enabled) check();
    return () => clearTimeout(timer.current);
  }, [enabled, check]);

  const checkAgain = useCallback(() => {
    setStatus("verifying");
    check();
  }, [check]);

  return { status, checkAgain };
};

export const PAYMENT_MESSAGES = {
  processing:
    "We're still confirming your payment with your bank. Please don't pay again. This page will update automatically. If it doesn't update within a few minutes, contact us and we'll check it for you.",
  failed:
    "Your payment did not go through. Please try booking again.",
};

export default usePaymentVerification;
